import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { QueryConfig } from '../api/client';
import type { ImpoundSortBy, Paginated } from '@relay-towing/api';
import { driverApi, DriverImpound } from '../services/driverApi';

interface ImpoundQueryParams {
  status?: 'ACTIVE' | 'RELEASED' | 'ALL';
  sortBy?: ImpoundSortBy;
  pageNo?: number;
  pageSize?: number;
  searchQuery?: string;
}

const getDriverImpounds = async (
  token: string,
  params?: ImpoundQueryParams,
  companyId?: string
): Promise<Paginated<DriverImpound>> => {
  console.log(
    '🎣 [useDriverImpounds] Setting auth token and calling API with params:',
    params
  );
  driverApi.setAuthToken(token);
  const result = await driverApi.getImpounds(params, companyId);
  console.log('🎣 [useDriverImpounds] Hook received result:', {
    dataLength: result.data.length,
    total: result.meta.totalCount,
  });
  return result;
};

export const useDriverImpounds = (
  params?: ImpoundQueryParams,
  config?: QueryConfig<typeof getDriverImpounds>
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      // Get driver profile to get company ID
      driverApi.setAuthToken(token);
      const driverProfile = await driverApi.getProfile();
      const companyId = driverProfile.companyId;

      console.log(
        '🎣 [useDriverImpounds] Got company ID from driver profile:',
        companyId
      );

      return getDriverImpounds(token, params, companyId);
    },
    queryKey: ['driver-impounds', params],
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes for impound updates
  });
};
